import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';

const ANONYMOUS_ID_KEY = 'ero_anonymous_id';

/**
 * Generates a UUID v4-like ID for anonymous tracking
 */
function generateAnonymousId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create an anonymous ID stored in localStorage
 */
function getOrCreateAnonymousId(): string {
  try {
    let id = localStorage.getItem(ANONYMOUS_ID_KEY);
    if (!id) {
      id = generateAnonymousId();
      localStorage.setItem(ANONYMOUS_ID_KEY, id);
    }
    return id;
  } catch {
    return generateAnonymousId();
  }
}

/**
 * Get UTM parameters from URL
 */
function getUtmParams(): { utm_source?: string; utm_medium?: string; utm_campaign?: string } {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('utm_campaign') || undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Detect device type from user agent
 */
function getDeviceType(): string {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua)) {
    if (/ipad|tablet/i.test(ua)) {
      return 'tablet';
    }
    return 'mobile';
  }
  return 'desktop';
}

/**
 * Detect browser from user agent
 */
function getBrowser(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera';
  return 'Unknown';
}

/**
 * Provider component that initializes anonymous tracking for unauthenticated users.
 * Creates a session and tracks the initial landing page view.
 */
export function AnonymousTrackingProvider(): null {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Only track if not authenticated
    if (isAuthenticated) return;

    const initAnonymousSession = async () => {
      const anonymousId = getOrCreateAnonymousId();
      const utmParams = getUtmParams();

      try {
        // Create anonymous session
        await fetch('/api/v1/analytics/anonymous/sessions', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            anonymous_id: anonymousId,
            device_type: getDeviceType(),
            browser: getBrowser(),
            referrer: document.referrer || undefined,
            landing_page: window.location.pathname,
            ...utmParams,
          }),
        });

        // Track initial landing page view
        await fetch('/api/v1/analytics/anonymous/events', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            anonymous_id: anonymousId,
            event_type: 'landing_page_view',
            event_category: 'navigation',
            page_url: window.location.pathname,
            referrer: document.referrer || undefined,
          }),
        });
      } catch {
        // Silent fail for analytics
      }
    };

    // Small delay to avoid race conditions with auth state
    const timeoutId = setTimeout(initAnonymousSession, 100);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated]);

  // Track page views for anonymous users on route changes
  useEffect(() => {
    if (isAuthenticated) return;

    const trackPageView = () => {
      const anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
      if (!anonymousId) return;

      fetch('/api/v1/analytics/anonymous/events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          anonymous_id: anonymousId,
          event_type: 'page_view',
          event_category: 'navigation',
          page_url: window.location.pathname,
          referrer: document.referrer || undefined,
        }),
      }).catch(() => {
        // Silent fail
      });
    };

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', trackPageView);

    return () => {
      window.removeEventListener('popstate', trackPageView);
    };
  }, [isAuthenticated]);

  return null;
}

export default AnonymousTrackingProvider;
